import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import * as Yup from "yup";

import { createRole, updateRole } from "src/api/settings";
import { Field, TextField } from "src/components/form";
import { Button } from "src/components/ui/button";
import { Checkbox } from "src/components/ui/checkbox";
import { SheetClose } from "src/components/ui/sheet";
import { usePortalContext } from "src/context/PortalContext";
import { PERMISSIONS } from "src/lib/constants";
import { showSuccessToast } from "src/lib/utils";
import type { Role } from "src/types/settings";

const validationSchema = Yup.object().shape({
	name: Yup.string().required("Role name is required"),
	permissions: Yup.array().of(Yup.string().required()).min(1, "Select at least one permission"),
});

interface RoleFormValues {
	uuid?: string;
	name: string;
	permissions: string[];
}

// `replaceAll` needs lib es2021; the tsconfig targets ES2020.
const permissionLabel = (permission: string) => permission.split("_").join(" ");

interface CreateRoleProps {
	/** Only supplied when editing an existing role. */
	role?: Role;
}

const CreateRole = ({ role }: CreateRoleProps) => {
	const queryClient = useQueryClient();
	const closePortal = usePortalContext();

	const initialValues: RoleFormValues = {
		...(!!role && { uuid: role.uuid }),
		name: role?.name ?? "",
		permissions: role?.permissions ?? [],
	};

	const { mutate, isLoading } = useMutation(role ? updateRole : createRole, {
		onSuccess: ({ message }) => {
			showSuccessToast(message ?? "");
			queryClient.invalidateQueries(["roles"]);
			queryClient.invalidateQueries(["user"]);
			closePortal();
		},
	});

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
			{({ handleSubmit, values, handleChange, setFieldValue, errors, touched }) => {
				const toggle = (permission: string) =>
					setFieldValue(
						"permissions",
						values.permissions.includes(permission)
							? values.permissions.filter((value) => value !== permission)
							: [...values.permissions, permission]
					);

				const allSelected = values.permissions.length === PERMISSIONS.length;

				return (
					<form onSubmit={handleSubmit} className="tw-flex tw-flex-col tw-gap-5">
						<TextField
							label="Role name"
							name="name"
							placeholder="Role name"
							value={values.name}
							error={touched.name && errors.name}
							onChange={handleChange}
							required
						/>

						<Field
							label="Permissions"
							error={touched.permissions && typeof errors.permissions === "string" ? errors.permissions : undefined}
							hint={`${values.permissions.length} of ${PERMISSIONS.length} selected`}
						>
							<div className="tw-mb-2 tw-flex tw-justify-end">
								<button
									type="button"
									onClick={() => setFieldValue("permissions", allSelected ? [] : [...PERMISSIONS])}
									className="tw-text-[12.5px] tw-font-semibold tw-text-teal hover:tw-underline"
								>
									{allSelected ? "Clear all" : "Select all"}
								</button>
							</div>

							<div className="tw-grid tw-gap-x-4 tw-gap-y-2.5 tw-rounded-[10px] tw-border tw-border-border tw-p-4 sm:tw-grid-cols-2">
								{PERMISSIONS.map((permission: string) => (
									<label key={permission} className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2.5">
										<Checkbox
											checked={values.permissions.includes(permission)}
											onCheckedChange={() => toggle(permission)}
										/>
										<span className="tw-text-[13.5px] tw-capitalize tw-text-text">{permissionLabel(permission)}</span>
									</label>
								))}
							</div>
						</Field>

						<div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
							<SheetClose asChild>
								<Button type="button" variant="ghost">
									Cancel
								</Button>
							</SheetClose>
							<Button type="submit" variant="secondary" disabled={isLoading}>
								{isLoading ? "Saving..." : "Save"}
							</Button>
						</div>
					</form>
				);
			}}
		</Formik>
	);
};

export default CreateRole;
